import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Clock, Users, Flame, ChefHat, Play, Plus, Wheat, ChevronDown } from "lucide-react";
import { api } from "@/lib/auth";
import { toast } from "sonner";
import PortionsScaler from "@/components/PortionsScaler";
import { scaleIngredients, parsePorciones } from "@/lib/scaling";

// Splits the `notas` string into the cook's notes and an optional gluten-free tip.
// The tip prefix lives in the source data as "🌾 Tip Sin Gluten: ..." (one line at the end).
function splitNotas(notas) {
  if (!notas) return { notes: "", glutenTip: "" };
  const marker = "🌾 Tip Sin Gluten:";
  const idx = notas.indexOf(marker);
  if (idx === -1) return { notes: notas.trim(), glutenTip: "" };
  return {
    notes: notas.slice(0, idx).trim(),
    glutenTip: notas.slice(idx + marker.length).trim(),
  };
}

export default function Recipe() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddToMenu, setShowAddToMenu] = useState(false);
  const [menus, setMenus] = useState([]);
  const [glutenOpen, setGlutenOpen] = useState(false);
  const [scaleFactor, setScaleFactor] = useState(1);

  const originalPorciones = useMemo(() => parsePorciones(r?.porciones), [r]);
  const scaledIngredientes = useMemo(
    () => scaleIngredients(r?.ingredientes || [], scaleFactor),
    [r, scaleFactor]
  );
  const scaledPersonas = Math.max(1, Math.round(originalPorciones * scaleFactor));

  useEffect(() => {
    api().get(`/recipes/${slug}`).then((res) => setR(res.data)).catch((err) => {
      toast.error("Receta no encontrada");
      nav("/cocina");
    }).finally(() => setLoading(false));
  }, [slug, nav]);

  const openAddToMenu = async () => {
    try {
      const res = await api().get("/menus");
      setMenus(res.data.menus || []);
      setShowAddToMenu(true);
    } catch (e) { toast.error("No pudimos cargar tus menús"); }
  };

  const addToMenu = async (m) => {
    const slugs = Array.from(new Set([...(m.recipe_slugs || []), slug]));
    await api().patch(`/menus/${m.id}`, { recipe_slugs: slugs });
    toast.success(`Agregado a "${m.name}"`);
    setShowAddToMenu(false);
  };

  const newMenuWithRecipe = async () => {
    const name = prompt('Nombre del menú (ej: "Comida Cumpleaños Anat")');
    if (!name) return;
    const res = await api().post("/menus", { name, recipe_slugs: [slug] });
    toast.success("Menú creado");
    nav(`/menu/${res.data.id}`);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-cocoa font-serif italic">Cargando…</div>;
  if (!r) return null;

  const photoUrl = r.photo || null;

  return (
    <div className="min-h-screen paper-texture iphone-safe-top">
      <header className="sticky top-0 z-30 bg-paper/95 backdrop-blur-md border-b border-line">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link data-testid="back-to-home" to="/cocina" className="p-2 rounded-full bg-cream border border-line"><ChevronLeft className="w-5 h-5 text-cocoa" /></Link>
          <p className="eyebrow text-[10px]">{r.categoria}</p>
          <button data-testid="add-to-menu-btn" onClick={openAddToMenu} className="p-2 rounded-full bg-cream border border-line" aria-label="Agregar a menú">
            <Plus className="w-5 h-5 text-cocoa" />
          </button>
        </div>
      </header>

      <article className="max-w-3xl mx-auto pb-32">
        {photoUrl ? (
          <img src={photoUrl} alt={r.nombre} className="w-full h-72 sm:h-96 object-cover" />
        ) : (
          <div className="w-full h-72 flex items-center justify-center bg-gradient-to-br from-amber-light/30 to-paper-warm">
            <ChefHat className="w-16 h-16 text-amber-deep/50" />
          </div>
        )}

        <div className="px-5 pt-6">
          {r.kosher_label && (
            <div
              data-testid={`kosher-badge-${r.kosher_label.tone}`}
              className={
                r.kosher_label.tone === "carne" ? "inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wider mb-3 bg-red-900/15 text-red-900 border border-red-900/30" :
                r.kosher_label.tone === "leche" ? "inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wider mb-3 bg-blue-800/15 text-blue-800 border border-blue-800/30" :
                r.kosher_label.tone === "parve" ? "inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wider mb-3 bg-emerald-800/15 text-emerald-800 border border-emerald-800/30" :
                "inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wider mb-3 bg-amber-500/15 text-amber-800 border border-amber-500/30"
              }
              title="Modo Kosher activo"
            >
              {r.kosher_label.tone === "carne" ? "🥩 " : r.kosher_label.tone === "leche" ? "🥛 " : r.kosher_label.tone === "parve" ? "🌿 " : "⚠️ "}
              {r.kosher_label.label}
            </div>
          )}
          {r.festividad && <p className="eyebrow mb-2">{r.festividad}</p>}
          {r.origen && !r.festividad && <p className="eyebrow mb-2">Origen · {r.origen}</p>}
          <h1 data-testid="recipe-title" className="font-display italic text-cocoa text-4xl sm:text-5xl leading-tight mb-3">{r.nombre}</h1>
          {r.descripcion_corta && <p className="font-serif text-cocoa-soft text-lg italic mb-5">{r.descripcion_corta}</p>}

          <div className="flex flex-wrap gap-3 mb-7 text-cocoa">
            {r.porciones && (
              <span className="inline-flex items-center gap-1.5 text-sm font-serif" data-testid="recipe-porciones">
                <Users className="w-4 h-4 text-amber-deep" />
                {scaleFactor === 1 ? r.porciones : `${scaledPersonas} personas`}
                {scaleFactor !== 1 && (
                  <span className="text-xs italic text-cocoa-soft ml-1">(original: {r.porciones})</span>
                )}
              </span>
            )}
            {r.tiempo_preparacion && <span className="inline-flex items-center gap-1.5 text-sm font-serif"><Clock className="w-4 h-4 text-amber-deep" />Prep: {r.tiempo_preparacion}</span>}
            {r.tiempo_coccion && <span className="inline-flex items-center gap-1.5 text-sm font-serif"><Flame className="w-4 h-4 text-amber-deep" />Cocción: {r.tiempo_coccion}</span>}
            {r.sin_gluten && <span className="px-2 py-0.5 rounded text-xs font-bold bg-[var(--moss)]/20" style={{color:'var(--moss)'}}>Sin gluten</span>}
          </div>

          {/* Escalador de porciones */}
          <PortionsScaler
            originalPorciones={originalPorciones}
            ingredientes={r.ingredientes || []}
            factor={scaleFactor}
            onFactorChange={setScaleFactor}
          />

          {/* Ingredients preview */}
          <h2 className="font-display italic text-cocoa text-2xl mb-3">Ingredientes</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-8" data-testid="ingredients-list">
            {scaledIngredientes.map((ingText, idx) => (
              <li key={idx} className="font-serif text-cocoa text-base flex gap-2">
                <span className="text-amber-deep">·</span>
                <span>{ingText}</span>
              </li>
            ))}
          </ul>

          {/* Instructions preview */}
          <h2 className="font-display italic text-cocoa text-2xl mb-3">Preparación</h2>
          <ol className="space-y-4 mb-10">
            {(r.instrucciones || []).map((s, idx) => (
              <li key={idx} className="flex gap-3">
                <span className="font-display italic text-amber-deep text-2xl leading-none w-8 shrink-0">{idx + 1}</span>
                <p className="font-serif text-cocoa text-base">{typeof s === "string" ? s : JSON.stringify(s)}</p>
              </li>
            ))}
          </ol>

          {(() => {
            const { notes, glutenTip } = splitNotas(r.notas);
            return (
              <>
                {notes && (
                  <div className="border-l-4 border-amber-deep pl-4 my-8 bg-cream/60 py-3 rounded-r">
                    <p className="eyebrow mb-1">Notas del Cocinero</p>
                    <p className="font-serif italic text-cocoa-soft whitespace-pre-line">{notes}</p>
                  </div>
                )}
                {glutenTip && (
                  <div data-testid="gluten-tip-block" className="my-8 rounded-xl border-2 overflow-hidden" style={{ borderColor: 'var(--moss)', background: 'rgba(107,122,78,0.08)' }}>
                    <button
                      data-testid="toggle-gluten-tip-btn"
                      type="button"
                      onClick={() => setGlutenOpen((v) => !v)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-[rgba(107,122,78,0.14)]"
                      aria-expanded={glutenOpen}
                    >
                      <span className="flex items-center gap-2.5">
                        <Wheat className="w-5 h-5" style={{ color: 'var(--moss)' }} />
                        <span className="font-display italic text-lg" style={{ color: 'var(--moss)' }}>
                          Ver versión Sin Gluten
                        </span>
                      </span>
                      <ChevronDown
                        className="w-5 h-5 transition-transform"
                        style={{ color: 'var(--moss)', transform: glutenOpen ? 'rotate(180deg)' : 'none' }}
                      />
                    </button>
                    {glutenOpen && (
                      <div data-testid="gluten-tip-content" className="px-4 pb-4 pt-1">
                        <p className="font-serif text-cocoa text-base leading-relaxed">{glutenTip}</p>
                        <p className="mt-3 text-xs italic text-cocoa-soft">
                          Sustituciones de papá pa' que toda la familia pueda comerla.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Sticky cook button */}
        <div className="fixed bottom-0 left-0 right-0 bg-paper/95 backdrop-blur-md border-t border-line p-4 iphone-safe-bottom">
          <div className="max-w-3xl mx-auto">
            <button data-testid="start-cooking-btn" onClick={() => nav(`/cocinar/${slug}`)} className="btn-primary w-full text-lg py-5">
              <Play className="w-5 h-5" /> Cocinar paso a paso
            </button>
          </div>
        </div>
      </article>

      {showAddToMenu && (
        <div className="fixed inset-0 z-50 bg-cocoa/40 flex items-end sm:items-center justify-center p-4" onClick={() => setShowAddToMenu(false)}>
          <div className="bg-cream rounded-t-2xl sm:rounded-2xl border border-line w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-cocoa text-2xl italic mb-4">Agregar a un menú</h3>
            {menus.length > 0 && (
              <ul className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                {menus.map((m) => (
                  <li key={m.id}>
                    <button data-testid={`add-to-menu-${m.id}`} onClick={() => addToMenu(m)} className="w-full text-left p-3 rounded border border-line hover:border-amber-deep transition font-serif text-cocoa">
                      {m.name} <span className="text-xs text-cocoa-soft">· {(m.recipe_slugs || []).length} recetas</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button data-testid="create-new-menu-btn" onClick={newMenuWithRecipe} className="btn-amber w-full">
              <Plus className="w-4 h-4" /> Crear nuevo menú
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
